import { get_coupon } from "@/services/woocommerce/cart-service";
import { get_user_country_or_detect } from "@/lib/location/country-detection";
import {
  hide_all_coupon_errors,
  show_coupon_error,
} from "./checkout-ui-actions.js";
import {
  is_challenge_restricted,
  is_platform_restricted,
} from "@/lib/location/restricted-countries";

import {
  set_subtotal_text,
  set_total_text,
  show_discount_section,
  hide_discount_section,
  set_discount_amount_text,
  set_coupon_code_display,
  show_payment_method_section,
  hide_payment_method_section,
  show_place_order_button,
  populate_select_options,
} from "./checkout-ui-actions.js";

import { COUPON_ERROR_CODES } from "@/lib/constants/payment-error-constants";

let product_variants = [];
let filtered_variants = [];
let applied_coupon = null;
let original_price = 0;
let discount_amount = 0;

let account_size_select = null;
let plan_select = null;
let platform_select = null;

/**
 * Formats a numeric amount as USD currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const format_currency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    amount,
  );

export const get_total_from_display = () => {
  const total_element = document.getElementById("total-display");
  if (!total_element || !total_element.textContent) return 0;
  const total_text = total_element.textContent.trim();
  const numeric_value = total_text.replace(/[^0-9.-]/g, "");
  const parsed_value = parseFloat(numeric_value);
  return Number.isNaN(parsed_value) ? 0 : parsed_value;
};

/**
 * Updates URL query parameters for variation and coupon
 * @param {{variation_id?: number|null, coupon?: string|null}} params - Values to set or clear
 * @returns {void}
 */
const update_url_parameters = (params) => {
  const url = new URL(window.location.href);
  if (Object.prototype.hasOwnProperty.call(params, "variation_id")) {
    const v = params.variation_id;
    if (v === null) url.searchParams.delete("variation_id");
    else if (v) url.searchParams.set("variation_id", String(v));
  }
  if (Object.prototype.hasOwnProperty.call(params, "coupon")) {
    const c = params.coupon;
    if (c === null) url.searchParams.delete("coupon");
    else if (c) url.searchParams.set("coupon", c);
  }
  window.history.replaceState({}, "", url.toString());
};

/**
 * Filters variants by country restrictions
 * @param {Array<Object>} variants - Variants to filter
 * @param {string} user_country - ISO country code
 * @returns {Array<Object>} Allowed variants
 */
const filter_variants_by_country = (variants, user_country) => {
  return variants.filter((variant) => {
    if (variant.account_size && variant.plan) {
      const challenge_restricted = is_challenge_restricted(
        user_country,
        variant.account_size,
        variant.plan,
      );
      if (challenge_restricted) return false;
    }
    if (variant.platform) {
      const platform_restricted = is_platform_restricted(
        user_country,
        variant.platform,
      );
      if (platform_restricted) return false;
    }
    return true;
  });
};

/**
 * Initializes DOM references and filtered variants
 * @returns {Promise<boolean>} Whether initialization succeeded
 */
const initialize_elements = async () => {
  const variants_data_element = document.getElementById("variants-data");
  if (!variants_data_element) return false;

  account_size_select = document.getElementById("account-size-select");
  plan_select = document.getElementById("plan-select");
  platform_select = document.getElementById("platform-select");

  product_variants = JSON.parse(
    variants_data_element?.dataset?.variants || "[]",
  );

  const user_country = await get_user_country_or_detect();
  filtered_variants = user_country
    ? filter_variants_by_country(product_variants, user_country)
    : product_variants;

  return true;
};

/**
 * Populates account size select options
 * @returns {void}
 */
const populate_account_sizes = () => {
  const values = [...new Set(filtered_variants.map((v) => v.account_size))];
  populate_select_options({ select_element: account_size_select, values });
};

/**
 * Updates plan options based on selected account size
 * @returns {void}
 */
const update_plan_options = () => {
  if (!account_size_select) return;
  const selected_account_size = account_size_select.value;
  const current_plan = plan_select?.value;
  const values = [
    ...new Set(
      filtered_variants
        .filter((v) => v.account_size === selected_account_size)
        .map((v) => v.plan),
    ),
  ];
  populate_select_options({ select_element: plan_select, values });
  if (current_plan && values.includes(current_plan))
    plan_select.value = current_plan;
};

/**
 * Updates platform options based on selected account size and plan
 * @returns {void}
 */
const update_platform_options = () => {
  if (!account_size_select || !plan_select) return;
  const selected_account_size = account_size_select.value;
  const selected_plan = plan_select.value;
  const current_platform = platform_select?.value;
  const values = [
    ...new Set(
      filtered_variants
        .filter(
          (v) =>
            v.account_size === selected_account_size &&
            v.plan === selected_plan,
        )
        .map((v) => v.platform),
    ),
  ];
  populate_select_options({ select_element: platform_select, values });
  if (current_platform && values.includes(current_platform))
    platform_select.value = current_platform;
};

/**
 * Finds the variant matching current selections
 * @returns {Object|null} Matching variant or null
 */
const find_matching_variant = () => {
  if (!account_size_select || !plan_select || !platform_select) return null;
  return (
    filtered_variants.find(
      (variant) =>
        variant.account_size === account_size_select.value &&
        variant.plan === plan_select.value &&
        variant.platform === platform_select.value,
    ) || null
  );
};

/**
 * Calculates discount and final price from coupon
 * @param {number} price - Original price
 * @param {{discount_type: string, amount: string}|null} coupon - Coupon data
 * @returns {{discount_amount: number, final_price: number}} Calculation result
 */
const calculate_discount = (price, coupon) => {
  if (!coupon) return { discount_amount: 0, final_price: price };
  let discount = 0;
  if (coupon.discount_type === "percent")
    discount = (price * parseFloat(coupon.amount)) / 100;
  else discount = parseFloat(coupon.amount);
  return {
    discount_amount: Math.min(discount, price),
    final_price: Math.max(0, price - discount),
  };
};

/**
 * Updates discount section and values in UI
 * @returns {void}
 */
const update_discount_display = () => {
  if (!applied_coupon || discount_amount <= 0) {
    hide_discount_section();
    set_coupon_code_display({ code: "" });
    return;
  }
  show_discount_section();
  set_coupon_code_display({ code: applied_coupon.code });
  set_discount_amount_text({ text: `-${format_currency(discount_amount)}` });
};

/**
 * Recalculates pricing and updates UI state
 * @returns {void}
 */
const update_pricing = () => {
  const variant = find_matching_variant();
  if (!variant) {
    original_price = 0;
    discount_amount = 0;
    const subtotal_default =
      document.getElementById("subtotal-display")?.dataset?.defaultText ||
      "Select options";
    const total_default =
      document.getElementById("total-display")?.dataset?.defaultText ||
      "Select options";
    set_subtotal_text({ text: subtotal_default });
    set_total_text({ text: total_default });
    update_discount_display();
    update_url_parameters({ variation_id: null });
    return;
  }

  original_price = variant.price;
  const discount_calc = calculate_discount(original_price, applied_coupon);
  discount_amount = discount_calc.discount_amount;
  const final_price = discount_calc.final_price;

  set_subtotal_text({ text: format_currency(original_price) });
  set_total_text({ text: format_currency(final_price) });
  update_discount_display();
  update_url_parameters({ variation_id: variant.variant_id });

  if (final_price <= 0) hide_payment_method_section();
  else show_payment_method_section();

  show_place_order_button();
};

/**
 * Handles country change by re-filtering and preserving selections
 * @param {string} new_country - New country code
 * @returns {void}
 */
const handle_country_change = (new_country) => {
  const country = String(new_country).toUpperCase();
  filtered_variants = filter_variants_by_country(product_variants, country);

  const current_account_size = account_size_select?.value || "";
  const current_plan = plan_select?.value || "";
  const current_platform = platform_select?.value || "";

  populate_account_sizes();
  if (current_account_size && account_size_select) {
    const account_size_options = Array.from(account_size_select.options).map(
      (opt) => opt.value,
    );
    if (account_size_options.includes(current_account_size)) {
      account_size_select.value = current_account_size;
      update_plan_options();
      if (current_plan && plan_select) {
        const plan_options = Array.from(plan_select.options).map(
          (opt) => opt.value,
        );
        if (plan_options.includes(current_plan)) {
          plan_select.value = current_plan;
          update_platform_options();
          if (current_platform && platform_select) {
            const platform_options = Array.from(platform_select.options).map(
              (opt) => opt.value,
            );
            if (platform_options.includes(current_platform)) {
              platform_select.value = current_platform;
            }
          }
        }
      }
    }
  }
  update_pricing();
};

/**
 * Applies a coupon and updates pricing state
 * @param {string} coupon_code - Coupon code to apply
 * @returns {Promise<boolean>} Whether application succeeded
 */
const apply_coupon_and_update_state = async (coupon_code) => {
  try {
    const current_variant = find_matching_variant();
    const email_input = document.getElementById("billing_email");
    const user_email = email_input?.value?.trim();

    const context = {
      cart_total: original_price,
      existing_coupons: applied_coupon ? [applied_coupon.code] : [],
      email: user_email,
      cart_product_id: current_variant ? current_variant.product_id : undefined,
      cart_variant_id: current_variant ? current_variant.variant_id : undefined,
    };

    const response = await get_coupon(coupon_code, context);

    if (response.error_code) {
      show_coupon_error({ error_type: response.error_code });
      return false;
    }
    if (response.success && response.data) {
      applied_coupon = response.data;
      update_pricing();
      update_url_parameters({ coupon: coupon_code });
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error applying coupon:", error);
    show_coupon_error({ error_type: COUPON_ERROR_CODES.COUPON_NETWORK_ERROR });
    return false;
  }
};

/**
 * Handles coupon apply button click and UI feedback
 * @returns {Promise<void>}
 */
const handle_apply_coupon = async () => {
  const coupon_input = document.getElementById("coupon");
  const coupon_button = document.getElementById("coupon-button");
  if (!coupon_input || !coupon_button) return;
  const coupon_code = coupon_input.value.trim();
  if (!coupon_code) {
    show_coupon_error({ error_type: COUPON_ERROR_CODES.COUPON_CODE_REQUIRED });
    return;
  }
  hide_all_coupon_errors();
  coupon_button.disabled = true;
  coupon_button.textContent =
    coupon_button.getAttribute("data-applying-text") || "Applying...";
  const success = await apply_coupon_and_update_state(coupon_code);
  if (success) {
    coupon_input.value = "";
    hide_all_coupon_errors();
  }
  coupon_button.disabled = false;
  coupon_button.textContent =
    coupon_button.getAttribute("data-original-text") || "Apply";
};

/**
 * Removes applied coupon and resets pricing state
 * @returns {void}
 */
export const handle_remove_coupon = () => {
  applied_coupon = null;
  original_price = 0;
  discount_amount = 0;
  hide_all_coupon_errors();
  set_coupon_code_display({ code: "" });
  update_pricing();
  update_url_parameters({ coupon: null });
};

/**
 * Applies coupon from URL if present
 * @returns {Promise<void>}
 */
const check_url_coupon = async () => {
  const url_params = new URLSearchParams(window.location.search);
  const coupon_code = url_params.get("coupon");
  if (!coupon_code) return;
  const success = await apply_coupon_and_update_state(coupon_code);
  if (success) {
    const coupon_input = document.getElementById("coupon");
    if (coupon_input) coupon_input.value = coupon_code;
  } else {
    update_url_parameters({ coupon: null });
  }
};

/**
 * Preselects variant from variation_id in URL
 * @returns {void}
 */
const preselect_variant_from_url = () => {
  const url_params = new URLSearchParams(window.location.search);
  const variation_id = url_params.get("variation_id");
  if (!variation_id || !filtered_variants.length) return;
  const selected_variant = filtered_variants.find(
    (v) => v.variant_id && String(v.variant_id) === String(variation_id),
  );
  if (!selected_variant) return;
  if (account_size_select)
    account_size_select.value = selected_variant.account_size;
  update_plan_options();
  if (plan_select) plan_select.value = selected_variant.plan;
  update_platform_options();
  if (platform_select) platform_select.value = selected_variant.platform;
  update_pricing();
};

/**
 * Wires change and click listeners for selects and coupon actions
 * @returns {void}
 */
const setup_event_listeners = () => {
  account_size_select?.addEventListener("change", () => {
    update_plan_options();
    update_platform_options();
    update_pricing();
  });
  plan_select?.addEventListener("change", () => {
    update_platform_options();
    update_pricing();
  });
  platform_select?.addEventListener("change", update_pricing);
  document
    .getElementById("coupon-button")
    ?.addEventListener("click", handle_apply_coupon);
  document
    .getElementById("remove-coupon-btn")
    ?.addEventListener("click", handle_remove_coupon);

  // Listens for billing country changes to re-filter available product variants.
  // It updates the account size, plan, and platform selects to only show options
  // allowed in the selected country, and refreshes the pricing UI.
  const billing_country_select = document.getElementById("billing_country");
  billing_country_select?.addEventListener("change", (event) => {
    const target = event.target;
    const new_country = target?.value;
    if (new_country) handle_country_change(new_country);
  });
};

/**
 * Wires region modal events to initialize and confirm flows
 * @returns {void}
 */
const setup_region_modal_listeners = () => {
  document.addEventListener("region-modal-confirm", () => {
    preselect_variant_from_url();
    show_place_order_button();
  });
  const region_modal = document.getElementById("region-modal");
  region_modal?.addEventListener("modal-shown", async () => {
    if (await initialize_elements()) {
      populate_account_sizes();
      update_plan_options();
      update_platform_options();
      update_pricing();
    }
  });
};

/**
 * Initializes purchase summary section and URL defaults
 * @returns {Promise<void>}
 */
export const init_purchase_summary_section = async () => {
  if (!(await initialize_elements())) return;
  populate_account_sizes();

  if (!account_size_select.listeners_attached) {
    setup_event_listeners();
    setup_region_modal_listeners();
    account_size_select.listeners_attached = true;
  }

  preselect_variant_from_url();
  await check_url_coupon();
};
